import { useEffect, useState } from "react";
import { Reveal } from "./Reveal";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const Retreats = () => {
  const [retreats, setRetreats] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api
      .get("/retreats")
      .then(({ data }) => setRetreats(data))
      .catch(() => toast.error("Could not load retreats."));
  }, []);

  const book = async (pkg) => {
    setLoadingId(pkg.id);
    try {
      const { data } = await api.post("/checkout/session", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoadingId(null);
    }
  };

  return (
    <section id="retreats" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-clay mb-4">Reserve your space</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-ink">
              Choose your retreat
            </h2>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-stretch">
          {retreats.map((pkg, i) => {
            const featured = i === 1;
            return (
              <Reveal key={pkg.id} delay={i * 0.12} className="h-full">
                <div
                  data-testid={`retreat-card-${pkg.id}`}
                  className={`h-full flex flex-col rounded-[2rem] p-8 transition-colors ${
                    featured ? "bg-forest text-sand" : "bg-surface text-ink"
                  }`}
                >
                  <p className={`text-xs tracking-[0.25em] uppercase ${featured ? "text-dune" : "text-clay"}`}>
                    {pkg.duration}
                  </p>
                  <h3 className="font-serif text-3xl mt-3">{pkg.name}</h3>
                  <p className={`mt-2 text-sm ${featured ? "text-sand/80" : "text-muted"}`}>{pkg.tagline}</p>

                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-serif text-5xl">${pkg.price.toLocaleString()}</span>
                    <span className={`text-sm ${featured ? "text-sand/70" : "text-muted"}`}>/ person</span>
                  </div>
                  {pkg.note && (
                    <p className={`mt-1 text-xs ${featured ? "text-sand/70" : "text-muted"}`}>{pkg.note}</p>
                  )}

                  <ul className="mt-8 space-y-3 flex-1">
                    {pkg.includes.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm">
                        <Check size={16} className={`mt-0.5 shrink-0 ${featured ? "text-dune" : "text-clay"}`} />
                        <span className={featured ? "text-sand/90" : "text-ink/80"}>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => book(pkg)}
                    disabled={loadingId === pkg.id}
                    data-testid={`book-btn-${pkg.id}`}
                    className={`mt-8 rounded-full px-7 py-4 text-sm transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60 ${
                      featured
                        ? "bg-sand text-forest hover:bg-dune"
                        : "bg-forest text-sand hover:bg-forest-deep"
                    }`}
                  >
                    {loadingId === pkg.id ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Redirecting…
                      </>
                    ) : (
                      "Book this retreat"
                    )}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
        <p className="text-center text-xs text-muted mt-8">Secure checkout powered by Stripe · Test mode</p>
      </div>
    </section>
  );
};
